import { useEffect, useState } from "react";

interface PhoneMockupProps {
  message?: string;
  senderName?: string;
  template?: {
    preview: string;
    type?: string;
  } | null;
  vars?: {
    customerName?: string;
    saleName?: string;
    discount?: string;
    promoCode?: string;
    expiryDate?: string;
  };
  cta?: {
    label: string;
    url?: string;
  };
  mediaFile?: File | null;
}

export const PhoneMockup: React.FC<PhoneMockupProps> = ({
  message,
  senderName = "Darwin Stores",
  template,
  vars,
  cta,
  mediaFile,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [resolvedText, setResolvedText] = useState<string>("");

  const varValues = [
    vars?.customerName || "[First_Name]",
    vars?.saleName || "[2]",
    vars?.discount || "[3]",
    vars?.promoCode || "[4]",
    vars?.expiryDate || "[5]",
  ];

  const hasMedia = template?.type === "Media + Text";
  const displayMessage = message || resolvedText || "Preview will appear here";
  const displaySenderName = senderName || "Darwin Stores";

  // Handle template resolution
  useEffect(() => {
    if (template && template.preview) {
      const resolved = template.preview.replace(/{{([1-9])}}/g, (_, n) => 
        varValues[parseInt(n) - 1] || `[${n}]`
      );
      setResolvedText(resolved);
    } else if (message) {
      setResolvedText(message);
    } else {
      setResolvedText("");
    }
  }, [template, message, vars]);

  // Handle media file object URL creation and cleanup
  useEffect(() => {
    // Clean up previous URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    // Create new URL only if mediaFile exists and is valid
    if (mediaFile && mediaFile instanceof File && hasMedia) {
      try {
        const url = URL.createObjectURL(mediaFile);
        setPreviewUrl(url);
      } catch (error) {
        console.error("Error creating object URL:", error);
        setPreviewUrl(null);
      }
    }

    // Cleanup function
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [mediaFile, hasMedia]);

  return (
    <div className="mx-auto w-full max-w-[280px]">
      <div className="relative bg-gray-100 rounded-[2.5rem] p-3 shadow-md border border-gray-200">
        {/* Top bar */}
        <div className="bg-gray-200 rounded-[2rem] overflow-hidden">
          {/* Status bar */}
          <div className="h-6 bg-gray-200 flex items-center justify-center">
            <div className="w-16 h-3 bg-gray-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="bg-white px-3 py-2 flex items-center gap-2 border-b border-gray-100">
            <button className="text-gray-400">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div className="flex-1 flex flex-col items-center">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mb-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                </svg>
              </div>
              <span className="text-[10px] text-gray-600 font-medium">{displaySenderName}</span>
            </div>

            <div className="w-6" />
          </div>

          {/* Messages area */}
          <div className="bg-white min-h-[160px] px-3 py-3 space-y-2">
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%]">
                {/* Media */}
                {hasMedia && previewUrl && (
                  <img
                    src={previewUrl}
                    alt="media"
                    className="w-full rounded-xl mb-2 object-cover max-h-[150px]"
                    onError={() => setPreviewUrl(null)}
                  />
                )}

                {hasMedia && !previewUrl && mediaFile && (
                  <div className="w-full h-28 bg-gray-200 rounded-xl mb-2 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}

                <p className="text-[11px] text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                  {displayMessage || (message ? message : "Preview will appear here")}
                </p>

                {cta?.label && (
                  <div className="mt-2 pt-2 border-t border-gray-300 flex items-center gap-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span className="text-[10px] font-medium text-emerald-600">{cta.label}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Input bar */}
          <div className="bg-white border-t border-gray-100 px-3 py-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            </div>

            <div className="flex-1 h-5 bg-gray-100 rounded-full" />

            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Usage examples:

// 1. Simple usage (original component)
// <PhoneMockup message="Hello world!" senderName="John Doe" />

// 2. Template usage (new component)
// <PhoneMockup 
//   template={{ preview: "Hello {{1}}!", type: "Text" }}
//   vars={{ customerName: "Alice" }}
//   cta={{ label: "Shop Now", url: "https://example.com" }}
//   mediaFile={selectedFile}
// />